// Tutorial content for tour.js. Copy is authored HTML (never user data). The
// `api` argument is built by the controller (see _tourApi in controller.js):
// permission/plan checks, a few state counts, and panel openers - so tour
// content never reaches into the model directly.
//
// Writing rule (CLAUDE.md): no em dashes in any of this copy.

import { PRACTICE_IDS } from "./practiceData.js";

const $ = (sel) => document.querySelector(sel);
const shown = (sel) => {
  const el = $(sel);
  return !!el && !el.classList.contains("hidden") && el.getClientRects().length > 0;
};

// Screen-state checks shared by the steps. These MUST test visibility, not
// presence: a closed Catalogue stays in the DOM (hidden), so "exists" stays
// true forever after the first visit and broke replays.
const visibleEl = (sel) => [...document.querySelectorAll(sel)].some((el) => el.getClientRects().length > 0 && !el.closest(".hidden"));
const catalogueOpen = () => visibleEl("#menuModal");
const orderOpen = () => visibleEl("#newOrderModal .pos-items-area, #newOrderModal .pos-empty");
const checkoutOpen = () => visibleEl("#newOrderModal .payment-method-toggle");
const panelOpen = (id) => () => shown(`#${id}`);

// Feature tours open their panel from the hub; the panel's own "?" button
// starts them with the panel already open.
const panelTour = ({ id, panel, title, icon, description, perm, open, steps }) => ({
  id,
  title,
  icon,
  description,
  can: (api) => !perm || api.can(perm),
  isOpen: panelOpen(panel),
  open: (api) => api.open[open](),
  helpAnchors: [{ selector: `#${panel} .cashflow-title, #${panel} .rp-title` }],
  steps,
});

const proNote = (api, feature, what) => (api.isLocked(feature)
  ? `<p class="tour-pro-note">✨ ${what} is part of <strong>Pointbunny Pro</strong>. You can see the summary now and unlock the rest anytime.</p>`
  : "");

// ── Main tour: your first sale ───────────────────────────────────────────────

const firstSale = {
  id: "first-sale",
  featured: true,
  title: "Your first sale",
  icon: "🛒",
  hero: "🐰",
  description: "Add your items, ring up a practice order, see the receipt and hand it over. About 3 minutes.",
  can: (api) => api.can("take_orders") || api.can("manage_menu"),
  // Checkout records a pretend sale while this tour runs (controller
  // _recordPracticeSale); the practice order is removed however it ends.
  practice: true,
  onStart: (api) => api.homeDefaults(true),
  onEnd: (api) => { api.clearPractice(); api.homeDefaults(false); },
  helpAnchors: [
    { selector: "#queueTitle", chapter: "queue" },
    { selector: "#newOrderModal .pos-title", chapter: "order" },
  ],
  chapters: [
    { id: "items", title: "Add your items", blurb: "Type them in or import a CSV" },
    { id: "screens", title: "Customer screen", blurb: "Let customers watch their order" },
    { id: "order", title: "Ring up an order", blurb: "Tap items into the cart" },
    { id: "checkout", title: "Payment & receipt", blurb: "Take payment, print or just save", entry: () => checkoutOpen() },
    { id: "queue", title: "Active Queue & pickup", blurb: "Mark it ready, then done" },
  ],
  steps: [
    {
      id: "welcome",
      chapter: "items",
      welcome: true,
      title: "Welcome to Pointbunny! 👋",
      body: `<p>Let's ring up a sale together, from adding your items all the way to handing the order over.</p>
             <p>It takes about 3 minutes. Close it anytime and pick up right where you left off from the <strong>?</strong> at the top.</p>`,
      tip: "This is <strong>practice mode</strong>. The sale you make isn't saved, nothing prints, and your numbers don't change. The practice order disappears as soon as the tutorial ends. (Items you add to your Catalogue are real and stay.)",
      nextLabel: "Let's go",
    },

    // ── Part 1: items ──
    {
      id: "open-catalogue",
      chapter: "items",
      skipIf: (api) => !api.can("manage_menu") || catalogueOpen(),
      target: "#menu-list",
      mode: "click",
      placement: "right",
      title: "Open your Catalogue",
      body: "<p>The Catalogue holds everything you sell: products, services, sizes and add-ons. Tap it to open.</p>",
    },
    {
      id: "csv",
      chapter: "items",
      skipIf: (api) => !api.can("manage_menu") || !catalogueOpen(),
      target: "#menuUploadCsvBtn",
      title: "Already have a list? Import it",
      body: `<p>If your items live in a spreadsheet, <strong>Upload CSV</strong> adds them all in one go. Inside there's a template that shows the format: one row per item, with optional sizes or flavors.</p>`,
      tip: "<strong>No need to tap it now.</strong> Just keep it in mind for later. Tap <strong>Next</strong> and we'll add one by hand.",
    },
    {
      id: "add-item",
      chapter: "items",
      skipIf: (api) => !api.can("manage_menu") || !catalogueOpen(),
      target: "#openAddModal",
      mode: (api) => (api.hasItems() ? "next" : "click"),
      title: (api) => (api.hasItems() ? "Add more anytime" : "Or add one by hand"),
      body: (api) => (api.hasItems()
        ? "<p>You already have items, nice. Use <strong>Add Item</strong> whenever you want to add another.</p>"
        : "<p>Tap <strong>Add Item</strong> to create your first product.</p>"),
      next: (api) => (api.hasItems() ? "inventory" : null),
    },
    {
      id: "item-form",
      chapter: "items",
      skipIf: () => !shown("#addMenuModal"),
      target: "#addMenuModal .modal-content",
      mode: "wait",
      signal: "item-added",
      waitHint: "Save your item to continue",
      placement: "right",
      title: "Name it and price it",
      body: `<p>Only a <strong>name</strong> and <strong>price</strong> are needed. A category, photo or emoji is optional.</p>
             <p>Sizes, flavors or add-ons go under <strong>Variants</strong>. Tap <strong>Save</strong> when you're done.</p>`,
      next: "inventory",
    },
    {
      id: "inventory",
      chapter: "items",
      skipIf: (api) => !api.can("manage_menu") || !catalogueOpen() || !api.hasItems(),
      target: ["#menuModal .menu-items-area .card", "#menuModal .menu-items-area"],
      title: "Want to track stock too?",
      body: (api) => `<p>Tap any item to edit it and set <strong>Inventory</strong> to <em>Simple count</em> or <em>Ingredients</em>. Stock goes down by itself with every sale, and you'll get low and out-of-stock alerts.</p>
             <p>It's optional. There's a separate <strong>Inventory tutorial</strong> waiting for you whenever you're ready (open Inventory and tap its <strong>?</strong>).</p>
             ${proNote(api, "inventory", "Inventory")}`,
    },
    {
      id: "close-catalogue",
      chapter: "items",
      skipIf: () => !catalogueOpen(),
      target: "#menuModal .menu-modal-header .modal-close",
      mode: "click",
      placement: "left",
      title: "Your catalogue is ready",
      body: "<p>Close the Catalogue and let's sell something.</p>",
    },

    // ── Part 2: customer screen ──
    {
      id: "customer-screen",
      chapter: "screens",
      target: "#displayDropdownToggle",
      title: "Show customers their order",
      body: `<p>Under <strong>Screens</strong> you'll find the <strong>Customer Screen</strong>. Put it on a second monitor or a tablet facing the counter.</p>
             <p>As you add items to an order, they appear there live with the running total, so customers can check it before paying.</p>`,
      tip: "Using a separate tablet? Sign in on it, open Customer Screen, and set its terminal number to match this one (the Terminal button up here).",
      action: { label: "Open it now", run: (api) => api.openCustomerDisplay() },
    },

    // ── Part 3: ring up an order ──
    {
      id: "new-order",
      chapter: "order",
      skipIf: () => orderOpen() || checkoutOpen(),
      target: "#newOrderBtn",
      mode: "click",
      title: "Start a new order",
      body: "<p>Tap <strong>New Order</strong> to open the register.</p>",
    },
    {
      id: "add-to-cart",
      chapter: "order",
      skipIf: (api) => checkoutOpen() || (api.cartCount() > 0 && !orderOpen()),
      target: [
        "#stockNoticeModal:not(.hidden) > *",
        ".item-modal-overlay:not(.hidden) .pos-item-detail-card",
        "#posItemsArea",
        "#newOrderModal .pos-empty",
      ],
      mode: (api) => (api.hasItems() ? "wait" : "next"),
      signal: "cart-add",
      waitHint: "Add an item to continue",
      placement: "left",
      title: (api) => (api.hasItems() ? "Tap an item to add it" : "Nothing to sell yet"),
      body: (api) => (api.hasItems()
        ? `<p>Items with sizes or add-ons ask you to pick options first, then <strong>Add to cart</strong>.</p>
             <p>Add as many as you like.</p>`
        : `<p>There are no active items to sell yet. Add an item in the Catalogue (or set one back to Active), then come back to this part from the <strong>?</strong>.</p>`),
      next: (api) => (api.hasItems() ? null : "finish"),
    },
    {
      id: "cart",
      chapter: "order",
      skipIf: () => !orderOpen(),
      target: [".pos-cart-sidebar", ".pos-cart-fab"],
      placement: "left",
      title: "What you see, they see",
      body: `<p>The cart updates as you go. If the <strong>Customer Screen</strong> is open, your customer is seeing these same items and the total right now.</p>`,
    },
    {
      id: "to-checkout",
      chapter: "order",
      skipIf: () => checkoutOpen(),
      target: [".pos-checkout-btn", ".pos-cart-fab"],
      mode: "click",
      title: "Head to checkout",
      body: "<p>Tap <strong>Checkout</strong> when the order is complete.</p>",
    },

    // ── Part 4: payment & receipt ──
    {
      id: "payment",
      chapter: "checkout",
      skipIf: () => shown("#printReceiptBtn"),
      target: ".pos-pay-panel",
      mode: "wait",
      until: () => shown("#printReceiptBtn"),
      waitHint: "Choose a payment method to continue",
      placement: "left",
      title: "How are they paying?",
      body: `<p>Pick <strong>Cash</strong>, <strong>Card</strong> or <strong>QR</strong>.</p>
             <p>For cash, type the amount they handed you and tap <strong>Enter</strong>. We'll work out the change.</p>`,
      tip: "Taking QR payments? Upload your payment QR code in <strong>Settings › Displays › Payment QR Code</strong>. When you pick QR here, it pops up on the Customer Screen for them to scan. You can add it now or anytime later.",
    },
    {
      id: "receipt",
      chapter: "checkout",
      target: "#printReceiptBtn",
      mode: "click",
      placement: "left",
      title: (api) => (api.printingOn() ? "Print the receipt" : "Record the sale"),
      body: (api) => (api.printingOn()
        ? "<p>On a real sale, <strong>Print Receipt</strong> saves the sale and prints it. You get a quick preview first to double-check. (In practice mode nothing prints.)</p>"
        : "<p>Printing is off, so <strong>Record Sale</strong> just saves the transaction. You get a quick preview first. (In practice mode nothing is saved.)</p>"),
      tip: (api) => (api.printingOn()
        ? "No receipt printer, or just want to save transactions? Turn printing off in <strong>Settings › POS</strong> and this button becomes Record Sale. Every sale is still kept under Sales &amp; Expenses."
        : "Want paper receipts? Turn printing on in <strong>Settings › POS</strong>. You can connect a Bluetooth printer there too."),
    },
    {
      id: "confirm",
      chapter: "checkout",
      target: [".confirm-overlay:not(.receipt-preview-overlay) > *", ".receipt-preview-overlay > *"],
      mode: "wait",
      signal: "sale-done",
      waitHint: "Confirm to finish the practice sale",
      title: "Check it, then confirm",
      body: (api) => `<p>This is exactly what goes on the receipt. On a real sale, <strong>Confirm</strong> saves it${api.printingOn() ? " and prints it" : ""}. Need a change? <strong>Go Back</strong> first.</p>
             <p>The invoice and ticket numbers fill in once you confirm.</p>`,
    },
    {
      id: "saved",
      chapter: "checkout",
      title: "Nice work! 🎉",
      body: `<p>That's how every sale goes. Real sales show up in today's numbers on the home screen and in <strong>Sales &amp; Expenses</strong>, where you can reprint or void them.</p>
             <p>Your practice order just landed in the Active Queue, tagged <strong>🎓 Practice</strong>. Let's look.</p>`,
    },
    {
      id: "settings",
      chapter: "checkout",
      target: ["#settingsBtn", "#navProfileBtn"],
      title: "Receipts your way",
      body: `<p><strong>Settings › POS</strong> (open it from your profile menu, top right) is where you turn receipt printing on or off, connect a Bluetooth printer, and choose what shows on the receipt.</p>
             <p>Printing off means sales are simply saved, perfect if you don't hand out paper receipts.</p>`,
    },

    // ── Part 5: active queue & pickup ──
    {
      id: "queue",
      chapter: "queue",
      target: ".home-queue-panel",
      placement: "left",
      title: "Your Active Queue",
      body: `<p>Every sale lands here as a ticket with its number, items and a timer. The bar under each one fills up as it gets close to late.</p>
             <p>Rows turn amber, then red, the longer an order waits. Change those timings in <strong>Settings › Order Queue Timers</strong>.</p>`,
    },
    {
      id: "mark-ready",
      chapter: "queue",
      target: [".status-popover.status-popover--open", "#openOrdersList .oq-row:has(.oq-practice) .oq-next-btn", "#openOrdersList .oq-row .oq-next-btn"],
      mode: (api) => (api.queueCount() > 0 ? "wait" : "next"),
      signal: ["order-ready", "order-done"],
      waitHint: "Mark the order ready to continue",
      placement: "left",
      title: "Mark it Ready for Pickup",
      body: (api) => (api.queueCount() > 0
        ? "<p>When the order is ready, tap <strong>Mark ready</strong> on its row. The <strong>⋯</strong> beside it has the other choices.</p>"
        : "<p>When an order is ready, tap <strong>Mark ready</strong> on its row. The <strong>⋯</strong> beside it has the other choices.</p>"),
    },
    {
      id: "pickup-screen",
      chapter: "queue",
      target: "#displayDropdownToggle",
      title: "Call out orders on the Pickup Screen",
      body: `<p><strong>Screens › Pickup Screen</strong> is a board for a TV or tablet your customers can see. Orders sit under <em>Preparing</em> and jump to <em>Ready for Pickup</em> the moment you mark them.</p>
             <p>In the kitchen? <strong>Screens › Queue Screen</strong> shows the same queue for your cooks.</p>`,
      tip: "Practice orders stay on this screen only, so you won't see this one on the Pickup or Queue Screen. Real orders show up there instantly.",
      action: { label: "Open Pickup Screen", run: (api) => api.openPickupDisplay() },
    },
    {
      id: "mark-done",
      chapter: "queue",
      skipIf: (api) => api.queueCount() === 0,
      target: [".status-popover.status-popover--open", "#openOrdersList .oq-row:has(.oq-practice) .oq-next-btn", "#openOrdersList .oq-row .oq-next-btn"],
      mode: "wait",
      signal: "order-done",
      waitHint: "Mark the order done to continue",
      placement: "left",
      title: "Hand it over, then Done",
      body: `<p>Once the customer has it, tap <strong>Done</strong> to clear it from the queue.</p>
             <p>Tapped it by mistake? You get 10 seconds to <strong>Undo</strong>.</p>`,
    },
    {
      id: "finish",
      chapter: "queue",
      target: ["#tourHubBtn", "#navProfileBtn", "#navMenuBtn"],
      title: "You're ready to sell 🎉",
      body: `<p>That's the whole loop: items, order, payment, receipt, queue, pickup. Your practice order is cleared when you tap Finish.</p>
             <p>Want a refresher later? Open <strong>Tutorials</strong> from here anytime to redo the tour, jump to one part, or open a guide for any other feature.</p>`,
      nextLabel: "Finish",
    },
  ],
};

// ── Feature guides ───────────────────────────────────────────────────────────

const catalogue = {
  id: "catalogue",
  title: "Catalogue",
  icon: "📦",
  description: "Items, variants, categories, CSV import and bulk edits.",
  can: (api) => api.can("manage_menu"),
  isOpen: catalogueOpen,
  open: (api) => api.open.catalogue(),
  helpAnchors: [{ selector: "#menuModal .menu-modal-title" }],
  steps: [
    {
      id: "actions",
      target: "#menuModal .menu-header-actions",
      title: "Add items your way",
      body: `<p><strong>Add Item</strong> creates one at a time. <strong>Upload CSV</strong> imports a whole list from a spreadsheet, with a template to start from.</p>`,
    },
    {
      id: "categories",
      target: "#menuModal .menu-cat-manage",
      title: "Categories",
      body: "<p>Group items into categories. They become the tabs on the New Order screen, so staff find things fast.</p>",
    },
    {
      id: "items",
      target: ["#menuModal .menu-items-area .card", "#menuModal .menu-items-area"],
      title: "Tap an item to edit it",
      body: `<p>Change its price, photo or emoji, variants (sizes, add-ons), tax exemption and status.</p>
             <p>Set it to <em>Unavailable</em> to grey it out for the day without deleting it. The <strong>Inventory</strong> section links it to stock.</p>`,
    },
    {
      id: "select",
      skipIf: () => !$("#menuSelectToggle"),
      target: "#menuSelectToggle",
      title: "Bulk edits",
      body: "<p><strong>Select</strong> lets you pick several items at once to change their status or delete them together.</p>",
    },
  ],
};

// Hands-on, on practice data (practiceData.js): the user sells lattes, watches
// the beans go Low (in Inventory AND on New Order), reorders with a purchase
// order, approves it, receives it, and sees stock + history update. The
// controller swaps the practice café in on start and the real data back on
// end; every Inventory write is fenced by _practiceGuard meanwhile.
const invRow = (api) => `#inventoryContent .inv-tr[data-id="${api.practice.ids.beans}"]`;
const inventory = {
  id: "inventory",
  title: "Inventory",
  icon: "🗃️",
  hero: "☕",
  description: "Hands-on with a practice café: sell lattes, watch beans run low, reorder and restock.",
  can: (api) => api.can("manage_inventory"),
  practice: true,
  resumable: false,
  onStart: (api) => api.practice.startInventory(),
  onEnd: (api) => { api.practice.end(); api.clearPractice(); },
  isOpen: panelOpen("inventoryPanel"),
  open: (api) => api.open.inventory(),
  helpAnchors: [{ selector: "#inventoryPanel .cashflow-title" }],
  steps: [
    {
      id: "welcome",
      welcome: true,
      title: "Let's run a café for a minute",
      body: `<p>Meet <strong>Bunny Café</strong>: a few drinks, a pastry, and the supplies behind them. You'll sell some lattes, watch the coffee beans run low, then reorder and restock them, start to finish.</p>`,
      tip: "This is <strong>practice data</strong>. Your real inventory is tucked away, untouched, and comes back the moment you finish or close this tutorial.",
      nextLabel: "Let's go",
    },
    {
      id: "stats",
      onEnter: (api) => api.practice.inventorySection("items"),
      target: "#inventoryContent .invx-stats",
      title: "Your stock at a glance",
      body: "<p>How many supplies you track, how many are running low or out, and what everything on your shelves is worth.</p>",
    },
    {
      id: "supplies",
      target: "#inventoryContent .inv-table",
      title: "Track what you sell, or what goes into it",
      body: `<p>Some things are counted as they are, like the <strong>Croissant</strong>: one sold, one gone (<em>Simple count</em>).</p>
             <p>Others are tracked by what goes into them: coffee beans by the gram, oat milk by the litre, cups by the piece (<em>Ingredients</em>).</p>
             <p>Each one has a <strong>reorder point</strong>. Beans get flagged the moment they drop to 500 g.</p>`,
    },
    {
      id: "open-beans",
      target: invRow,
      mode: "click",
      title: "Tap Coffee beans",
      body: "<p>Let's see what uses them.</p>",
    },
    {
      id: "used-by",
      target: "#inventoryDrawer",
      placement: "left",
      title: "How stock goes down by itself",
      body: `<p><strong>Used by</strong> lists every product that takes beans: each Latte and Cappuccino uses 18 g. Ring one up and the beans go down automatically. No counting by hand.</p>
             <p>Just above it are the suppliers you buy beans from, with their prices.</p>`,
    },
    {
      id: "sell",
      onEnter: (api) => api.practice.inventorySection("items"),
      target: invRow,
      mode: "wait",
      signal: "practice-sold",
      waitHint: "Tap Sell 40 lattes",
      title: "Busy morning!",
      body: "<p>Ring up 40 lattes and keep your eye on the beans: 40 × 18 g = 720 g used.</p>",
      action: { label: "☕ Sell 40 lattes", run: (api) => api.practice.sellLattes() },
    },
    {
      id: "low",
      target: ["#inventoryContent .inv-attn:not(.inv-attn--po)", "#inventoryContent .invx-stats"],
      title: "Beans are running low",
      body: `<p>They dropped to 480 g, under the 500 g reorder point. The row turns <strong>Low</strong>, the Low stock tile counts it, and this banner pops up so nobody misses it.</p>`,
    },
    {
      id: "new-order",
      skipIf: (api) => !api.can("take_orders"),
      onEnter: (api) => api.practice.showNewOrder(),
      target: `#newOrderModal .pos-item-card[data-id="${PRACTICE_IDS.latte}"]`,
      placement: "bottom",
      title: "Your cashiers see it too",
      body: `<p>On New Order, the Latte and Cappuccino now wear a <strong>LOW</strong> chip (both use beans). It never blocks a sale, it just gives a heads-up before someone promises ten lattes.</p>
             <p>Tapping the chip shows which supply is low.</p>`,
    },
    {
      id: "reorder",
      onEnter: (api) => { if (document.querySelector("#newOrderModal .pos-items-area")) api.practice.showInventory(); },
      target: "#invAttnReorder",
      mode: "click",
      title: "Reorder in one tap",
      body: "<p>Tap <strong>🛒 Reorder</strong> to start a purchase order for everything that's low.</p>",
    },
    {
      id: "po-form",
      target: "body > .modal-overlay-form .invx-modal-card--wide",
      mode: "wait",
      signal: "po-created",
      waitHint: "Tap Create - for approval",
      placement: "left",
      title: "Check the order",
      body: `<p>It's filled in for you: enough beans to get back to a comfortable level, priced at what you paid last time.</p>
             <p>Pick <strong>Bean There Roasters</strong> as the supplier if you like, then tap <strong>Create - for approval</strong>.</p>`,
    },
    {
      id: "approve",
      onEnter: (api) => api.practice.focusNewestPo(),
      target: '#inventoryDrawer [data-act="send"]',
      mode: "click",
      placement: "left",
      title: "Approve it",
      body: `<p>New orders wait <strong>For approval</strong>, so someone can check them before money is spent.</p>
             <p>Tap <strong>Approve order</strong> once you've placed it with the supplier.</p>`,
    },
    {
      id: "receive",
      target: '#inventoryDrawer [data-act="receive"]',
      mode: "click",
      placement: "left",
      title: "Delivery day",
      body: `<p>The order is now <strong>Waiting for delivery</strong> (and turns Overdue if it's late). When the boxes arrive, tap <strong>Mark as received</strong>.</p>`,
    },
    {
      id: "receive-confirm",
      target: "body > .modal-overlay-form .invx-modal-card",
      mode: "wait",
      signal: "po-received",
      waitHint: "Tap Add to stock",
      title: "Add it to stock",
      body: "<p>Confirm, and every line on the order is added to your stock in one go.</p>",
    },
    {
      id: "restocked",
      onEnter: (api) => api.practice.inventorySection("items"),
      target: invRow,
      title: "Back to OK",
      body: `<p>Beans are topped up and the Low flag is gone, here and on New Order.</p>
             <p>The cost per gram updates to what you just paid, so your inventory value stays accurate.</p>`,
    },
    {
      id: "history",
      onEnter: (api) => api.practice.inventorySection("history"),
      target: "#inventoryContent",
      placement: "left",
      title: "Every movement, logged",
      body: "<p>Each sale, delivery, stock count and manual change, with who did it. Filter by type or date when something doesn't add up.</p>",
    },
    {
      id: "rest",
      target: "#inventorySidebar",
      placement: "right",
      title: "The rest of Inventory",
      body: `<p><strong>Purchase Orders</strong> lists every order. <strong>Suppliers</strong> keeps contacts and prices. <strong>Stock Counts</strong> is for walking the shelves and fixing the numbers to match what's really there.</p>`,
    },
    {
      id: "finish",
      title: "Now make it yours",
      body: (api) => `<p>1. Add your supplies here with <strong>New Item</strong>.<br>2. Open a product in the Catalogue and set its Inventory to <em>Simple count</em> or <em>Ingredients</em>.<br>3. Set reorder points, and Pointbunny keeps watch.</p>
             ${proNote(api, "inventory", "Inventory")}`,
      tip: "Your real inventory comes back as soon as you tap Finish.",
      nextLabel: "Finish",
    },
  ],
};

const discounts = panelTour({
  id: "discounts",
  panel: "discountPanel",
  open: "discounts",
  perm: "manage_adjustments",
  title: "Receipt & Tax",
  icon: "🧾",
  description: "Sales tax or VAT, service charges and per-item exemptions.",
  steps: [
    {
      id: "tax",
      target: "#discountPanel .tax-settings-card",
      title: "How you charge tax",
      body: `<p>Choose whether tax is <strong>added at checkout</strong>, or already <strong>included</strong> in your prices (VAT style), and set your rate. We mark the option and rate recommended for your location.</p>
             <p>You can also exempt specific items.</p>`,
    },
    {
      id: "auto",
      target: "#adjOnTopCard",
      title: "Automatic adjustments",
      body: "<p>Fees and discounts listed here are applied to every receipt, like a service charge. Switch any of them on or off. Sales tax lives in Tax Settings above.</p>",
    },
    {
      id: "add",
      target: "#adjPanelAddBtn",
      title: "Add one",
      body: "<p>Create a percentage or fixed amount. Cashiers can still adjust or remove it on a single receipt at checkout.</p>",
    },
  ],
});

const promos = panelTour({
  id: "promos",
  panel: "promoPanel",
  open: "promos",
  perm: "manage_discounts",
  title: "Promo codes",
  icon: "🎟️",
  description: "Create discount codes customers can use at checkout.",
  steps: [
    {
      id: "new",
      target: "#promoNewBtn",
      title: "Create a code",
      body: "<p>Make a code like <em>WELCOME10</em> for a percentage or fixed discount, with optional limits and an end date.</p>",
    },
    {
      id: "stats",
      target: "#promoStats",
      title: "See what's working",
      body: "<p>A quick view of active codes and how often they're used.</p>",
    },
    {
      id: "use",
      title: "Using a code",
      body: "<p>At checkout, type the code in the promo field and the discount is applied to that receipt.</p>",
    },
  ],
});

const loyalty = panelTour({
  id: "loyalty",
  panel: "loyaltyPanel",
  open: "loyalty",
  perm: "manage_loyalty",
  title: "Loyalty",
  icon: "⭐",
  description: "Reward regulars with points, discounts and free items.",
  steps: [
    {
      id: "intro",
      title: "Turn regulars into fans",
      body: (api) => `<p>Customers join with their phone number at checkout, earn points on every purchase, and spend them on rewards.</p>${proNote(api, "loyalty", "Loyalty")}`,
    },
    {
      id: "settings",
      target: "#loyaltySettingsCard",
      title: "How points work",
      body: "<p>Switch the program on, then set how many points a purchase earns and what points are worth as a discount.</p>",
    },
    {
      id: "rewards",
      target: "#loyaltyPanel .loyalty-rewards-card",
      title: "Free-item rewards",
      body: "<p>Offer specific items for a set number of points, like a free coffee for 100 points.</p>",
    },
    {
      id: "customers",
      target: "#loyaltyPanel .loyalty-customers-card",
      title: "Your members",
      body: "<p>Look anyone up, see their points and visit streak, or add a member by hand.</p>",
    },
  ],
});

const staff = panelTour({
  id: "staff",
  panel: "staffPanel",
  open: "staff",
  perm: "manage_staff",
  title: "Staff & roles",
  icon: "👥",
  description: "Invite your team and control what each person can do.",
  steps: [
    {
      id: "invite",
      target: "#inviteStaffBtn",
      title: "Invite your team",
      body: "<p>Send an invite by email. Each person gets their own login and a 6-digit PIN for switching cashiers at the register.</p>",
    },
    {
      id: "tabs",
      target: "#staffSidebar",
      placement: "right",
      title: "Members and Roles",
      body: "<p><strong>Members</strong> lists everyone on your team. <strong>Roles</strong> decides what each role can see and do, like taking orders, voiding sales or viewing reports.</p>",
    },
    {
      id: "switch",
      title: "Switching cashiers",
      body: "<p>On the home screen, tap the <strong>Cashier</strong> tile to switch who's at the register. Every sale records who rang it up.</p>",
    },
  ],
});

const schedule = panelTour({
  id: "schedule",
  panel: "schedulePanel",
  open: "schedule",
  perm: "manage_schedule",
  title: "Shift schedule",
  icon: "🗓️",
  description: "Plan the week, then publish it so your team sees their shifts.",
  steps: [
    {
      id: "grid",
      target: "#scheduleContent .sch-grid-wrap",
      title: "Your week at a glance",
      body: "<p>Your team runs down the side and the days run across. Tap the <strong>+</strong> in a day to add a shift, or tap a shift to change it. Leave <strong>Who</strong> empty to post an open shift. Striped days mean someone is on leave or unavailable.</p>",
    },
    {
      id: "publish",
      target: "#schPublishBtn",
      title: "Publish when it's ready",
      body: "<p>New shifts start as <strong>drafts</strong> (dashed edges) that only schedulers see. <strong>Publish week</strong> sends them to your team's Time Clock.</p>",
    },
    {
      id: "copy",
      target: "#schCopyBtn",
      title: "Copy last week",
      body: "<p>Most weeks look alike. Copy last week's shifts as drafts, adjust, then publish.</p>",
    },
    {
      id: "stations",
      target: "#scheduleSidebar",
      placement: "right",
      title: "Requests, availability, stations",
      body: "<p><strong>Requests</strong> holds shift swaps and open-shift pickups waiting for your OK. <strong>Availability</strong> shows when each person can work. <strong>Stations</strong> are the labels on shifts, like Barista or Kitchen.</p>",
    },
  ],
});

// Hands-on, on a practice team (practiceData.js): fix a forgotten clock-out,
// approve a correction and a day off, tour the Time Clock setup and export.
// Every Timesheets write is fenced by _practiceGuard while it runs.
const fillClockOut = () => {
  const inEl = document.getElementById("shiftClockIn");
  const outEl = document.getElementById("shiftClockOut");
  if (!inEl?.value || !outEl) return;
  outEl.value = `${inEl.value.slice(0, 10)}T20:00`;
  outEl.dispatchEvent(new Event("input", { bubbles: true }));
};
const timesheets = {
  id: "timesheets",
  title: "Timesheets & Time Clock",
  icon: "⏱️",
  hero: "⏱️",
  description: "Hands-on with a practice team: fix a missed clock-out, approve requests, set up the time clock.",
  can: (api) => api.can("manage_pay") || api.can("manage_staff"),
  resumable: false,
  onStart: (api) => api.practice.startTimesheets(),
  onEnd: (api) => api.practice.end(),
  isOpen: panelOpen("timesheetPanel"),
  open: (api) => api.open.timesheets(),
  helpAnchors: [{ selector: "#timesheetPanel .cashflow-title" }],
  steps: [
    {
      id: "welcome",
      welcome: true,
      title: "Meet the Bunny Café team",
      body: `<p>Maya, Jon and Ana worked last week. You'll fix a forgotten clock-out, approve a correction and a day off, and see how the Time Clock works.</p>`,
      tip: (api) => `This is <strong>practice data</strong>. Your real timesheets are untouched and come back when you finish or close this tutorial.${api.isLocked("timesheets") ? " Timesheets is part of Pointbunny Pro." : ""}`,
      nextLabel: "Let's go",
    },
    {
      id: "stats",
      target: "#timesheetContent .ts-stat-strip",
      title: "The week at a glance",
      body: "<p>Total hours, shifts worked, and the average shift length for everyone on the team.</p>",
    },
    {
      id: "open-jon",
      target: `#timesheetContent [data-staff-toggle][data-staff-id="${PRACTICE_IDS.jon}"]`,
      mode: "click",
      title: "Open Jon's week",
      body: "<p>Everyone gets a card with their total hours. Tap <strong>Jon Reyes</strong>.</p>",
    },
    {
      id: "edit-open",
      target: `#timesheetContent .payroll-edit-btn[data-id="${PRACTICE_IDS.jonOpenShift}"]`,
      mode: "click",
      title: "Jon forgot to clock out",
      body: `<p>Thursday's shift has no end time, so it still says <strong>Active</strong> and doesn't count toward his hours.</p>
             <p>Tap <strong>Edit</strong> to fix it.</p>`,
    },
    {
      id: "save-shift",
      target: "#shiftEditModal .cashier-picker-card",
      mode: "wait",
      signal: "shift-saved",
      waitHint: "Set the clock-out, then tap Save",
      placement: "right",
      title: "Add the clock-out time",
      body: "<p>Jon left at 8:00 PM. Set <strong>Clock Out</strong>, then tap <strong>Save</strong>. Every edit is labeled with who made it.</p>",
      action: { label: "Fill in 8:00 PM for me", run: () => fillClockOut() },
    },
    {
      id: "corrections-tab",
      target: '#timesheetSidebar .ts-tab[data-section="corrections"]',
      mode: "click",
      placement: "right",
      title: "Staff can fix their own mistakes",
      body: "<p>When someone forgets to clock in or out, they can request a fix from the Time Clock. Those requests land here. Tap <strong>Corrections</strong>.</p>",
    },
    {
      id: "correction",
      target: "#timesheetContent .ser-req",
      mode: "wait",
      signal: "correction-reviewed",
      waitHint: "Approve or reject to continue",
      title: "Ana's correction",
      body: `<p>She says she started at 9:00, not 9:20. <strong>Approve</strong> applies the new time to her shift. <strong>Reject</strong> keeps it as it was.</p>`,
    },
    {
      id: "leave-tab",
      target: '#timesheetSidebar .ts-tab[data-section="leave"]',
      mode: "click",
      placement: "right",
      title: "Days off",
      body: "<p>Vacation and sick-day requests come in here too. Tap <strong>Leave</strong>.</p>",
    },
    {
      id: "leave",
      target: "#timesheetContent .ts-lv-card",
      mode: "wait",
      signal: "leave-reviewed",
      waitHint: "Approve or reject to continue",
      title: "Jon wants next Friday off",
      body: "<p>Approve it and it moves to <strong>Upcoming</strong>, so you can plan the week around it.</p>",
    },
    {
      id: "timeclock-tab",
      target: '#timesheetSidebar .ts-tab[data-section="timeclock"]',
      mode: "click",
      placement: "right",
      title: "Where hours come from",
      body: "<p>Tap <strong>Time Clock</strong>.</p>",
    },
    {
      id: "timeclock",
      target: "#timesheetContent",
      placement: "left",
      title: "Set up a Time Clock",
      body: `<p>Put a tablet by the door and open the <strong>Time Clock</strong> on it (Time Clock in the side menu). Generate a code here, enter it on the tablet once, and it's ready.</p>
             <p>Staff then clock in, take breaks and clock out with their PIN, and request corrections or days off from the same screen.</p>`,
      tip: "Every device you set up is listed here, so a lost tablet can be removed in one tap.",
    },
    {
      id: "back",
      target: '#timesheetSidebar .ts-tab[data-section="timesheets"]',
      mode: "click",
      placement: "right",
      title: "Back to the hours",
      body: "<p>Tap <strong>Timesheets</strong>.</p>",
    },
    {
      id: "export",
      target: "#payrollExportToggle",
      title: "Payroll time",
      body: "<p>Export the period as a PDF or CSV for payroll. Switch between week, month or a custom range at the top.</p>",
    },
    {
      id: "finish",
      title: "Ready for payroll",
      body: `<p>Set up a Time Clock device, give your team their PINs from <strong>Staff</strong>, and their hours land here on their own.</p>`,
      tip: "Your real timesheets come back as soon as you tap Finish.",
      nextLabel: "Finish",
    },
  ],
};

const reports = panelTour({
  id: "reports",
  panel: "reportsPanel",
  open: "reports",
  perm: "view_reports",
  title: "Reports",
  icon: "📊",
  description: "Sales trends, best sellers, busy hours and serving times.",
  steps: [
    {
      id: "period",
      target: "#reportsTabs",
      title: "Pick a period",
      body: (api) => `<p>Switch between today, this week, month, year or a custom range.</p>${proNote(api, "reports", "Reports beyond today")}`,
    },
    {
      id: "sections",
      target: "#reportsPanel .rp-sidebar",
      placement: "right",
      title: "Dig into the numbers",
      body: "<p><strong>Overview</strong> for the big picture, <strong>Sales</strong> for best sellers, <strong>Traffic</strong> for busy hours, <strong>Kitchen</strong> for serving times, <strong>Channels</strong> for order types and payment methods.</p>",
    },
    {
      id: "compare",
      target: "#reportsCompareBtn",
      title: "Compare periods",
      body: "<p>Put this week next to last week, or this month next to the same month last year.</p>",
    },
    {
      id: "export",
      target: "#reportsExportBtn",
      title: "Take it with you",
      body: "<p>Export to CSV for spreadsheets, or PDF to share.</p>",
    },
  ],
});

const cashflow = panelTour({
  id: "cashflow",
  panel: "cashflowPanel",
  open: "cashflow",
  perm: "manage_cashflow",
  title: "Sales & expenses",
  icon: "💰",
  description: "Every transaction, reprints, voids and expenses.",
  steps: [
    {
      id: "tabs",
      target: "#cashflowPanel .cashflow-tabs",
      title: "Sales, expenses, voided",
      body: "<p>Every sale is saved here, whether or not a receipt was printed. Expenses and voided sales have their own tabs.</p>",
    },
    {
      id: "sale",
      target: "#cashflowSalesList",
      title: "Tap a sale",
      body: "<p>See its receipt, <strong>reprint</strong> it, or <strong>void</strong> it if it was a mistake. Voided sales keep their invoice number so your records stay complete.</p>",
    },
    {
      id: "expense",
      target: "#addExpenseBtn",
      title: "Log expenses",
      body: "<p>Record money going out, like supplies or rent, to see your real net for the day.</p>",
    },
    {
      id: "export",
      target: "#exportCsvBtn",
      title: "Export",
      body: "<p>Download what you're looking at as a CSV for your accountant.</p>",
    },
  ],
});

const screens = {
  id: "screens",
  title: "Screens & terminals",
  icon: "🖥️",
  description: "Customer, queue and pickup screens, plus pairing devices.",
  can: () => true,
  steps: [
    {
      id: "screens",
      target: "#displayDropdownToggle",
      title: "Three extra screens",
      body: `<p><strong>Customer Screen</strong> shows the order and total as you ring it up.<br>
             <strong>Queue Screen</strong> is for the kitchen or prep area.<br>
             <strong>Pickup Screen</strong> is a board customers watch for their number.</p>`,
    },
    {
      id: "terminal",
      target: "#terminalToggle",
      title: "Pair the right devices",
      body: "<p>If you run more than one register, give each a terminal number. Set a customer screen to the same number so it only shows that register's orders.</p>",
    },
    {
      id: "settings",
      target: ["#settingsBtn", "#navProfileBtn"],
      title: "Make them yours",
      body: "<p><strong>Settings › Displays</strong> (in your profile menu, top right) lets you add a promo image to the customer and pickup screens, upload a payment QR code, and set window sizes.</p>",
    },
  ],
};

export const TOURS = [firstSale, catalogue, inventory, discounts, promos, loyalty, staff, timesheets, schedule, reports, cashflow, screens];
